import React, { FC, useMemo } from 'react';
import { Procedure } from '@/types/Procedure';
import { fullNameToLastNameRest } from '../shared/procedureUtils';
import { AnesthesiologistShiftRoom } from '@/types/AnesthesiologistShiftRoom';
import { StaffShiftRoomContext } from '../staff/StaffShiftContext';
import { format } from 'date-fns';
import { StaffSlotRoomContext } from '@/components/pages/kiosk/schedule/staff/StaffSlotContext';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { Anesthesiologist } from '../anesthesiologist/PersonnelRoom';
import StaffShifts from '@/components/pages/kiosk/schedule/staff/StaffShifts';
import StaffShiftMenu from '@/components/pages/kiosk/schedule/staff/StaffShiftMenu';
import { PrimarySpecialty } from '@/types/StaffMember';
import { useScope } from '@/hooks/useScope';
import { useStylesOverview } from '../overview/Overview';
import StaffShift from '@/types/StaffShift';
import { RoomType } from '@/types/Room';
import { makeStyles } from '@material-ui/core/styles';

type TimelineRoomProps = {
  id: number;
  name: string;
  type: RoomType;
  staffShifts: StaffShift[];
  anesthesiologistShifts: AnesthesiologistShiftRoom[];
  secondaryAnesthesiologistShifts: AnesthesiologistShiftRoom[];
  procedures: Procedure[];
  editableAnesthesiologist: boolean;
  condensView: boolean;
  isAnesthesiologistSchedule: boolean;
  editableStaff: boolean;
  isKiosk: boolean;
  date: Date;
};

const TimelineRoomComponent: FC<TimelineRoomProps> = ({
  id,
  name,
  type,
  staffShifts,
  anesthesiologistShifts,
  secondaryAnesthesiologistShifts,
  procedures,
  editableAnesthesiologist,
  condensView,
  isAnesthesiologistSchedule,
  editableStaff,
  isKiosk,
  date,
}) => {
  const scope = useScope();
  const stylesOverview = useStylesOverview();
  const styles = useStyles();

  const hospitalId = scope?.hospital.id;

  const secondaryRoomAnesthesiologist = Boolean(scope?.hospital?.modules?.secondaryRoomAnesthesiologist);
  const autoDischarge = Boolean(scope?.hospital?.modules?.autoDischarge);
  const canAddStaff = !isKiosk && editableStaff;

  const anesthesiologistName = anesthesiologistShifts?.[0]?.anesthesiologist?.name;
  const secondaryAnesthesiologistName = secondaryAnesthesiologistShifts?.[0]?.anesthesiologist?.name;

  const formattedAnesthesiologistName = useMemo(
    () => fullNameToLastNameRest(anesthesiologistName),
    [anesthesiologistName]
  );

  const formattedSecondaryAnesthesiologistName = useMemo(
    () => fullNameToLastNameRest(secondaryAnesthesiologistName),
    [secondaryAnesthesiologistName]
  );

  const anesthesiologistShiftRoomId = anesthesiologistShifts?.[0]?.id;
  const secondaryAnesthesiologistShiftRoomId = secondaryAnesthesiologistShifts?.[0]?.id;

  const proceduresInRoom = useMemo(
    () => (procedures || []).filter(procedure => procedure?.orId === id),
    [procedures, id]
  );

  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);

  return (
    <StaffShiftRoomContext
      key={`timeline-room-${id}`}
      hospitalId={hospitalId}
      roomId={id}
      date={formattedDate}
      staffShifts={staffShifts}
    >
      <StaffSlotRoomContext hospitalId={hospitalId} roomId={id} date={formattedDate}>
        <Box className={stylesOverview.staffWrapper}>
          <Box className={stylesOverview.roomHeader}>
            {!isAnesthesiologistSchedule && canAddStaff && (
              <Box mr={1}>{!!id && <StaffShiftMenu defaultPrimarySpecialty={PrimarySpecialty.OR} />}</Box>
            )}
            <Box flex={1} textAlign="center">
              <Typography variant="h5" className={styles.roomTitle}>
                {name}
              </Typography>
            </Box>
            <Box mr={1}>
              {!!id && (
                <Anesthesiologist
                  date={date}
                  roomId={id}
                  condensView={condensView}
                  editableAnesthesiologist={editableAnesthesiologist}
                  anesthesiologistName={formattedAnesthesiologistName}
                  secondaryAnesthesiologistName={formattedSecondaryAnesthesiologistName}
                  anesthesiologistShiftRoomId={anesthesiologistShiftRoomId}
                  secondaryAnesthesiologistShiftRoomId={secondaryAnesthesiologistShiftRoomId}
                  procedureQuery={proceduresInRoom}
                  roomType={type}
                  secondaryRoomAnesthesiologist={secondaryRoomAnesthesiologist}
                  autoDischarge={autoDischarge}
                />
              )}
            </Box>
          </Box>
          {!isAnesthesiologistSchedule && staffShifts.length !== 0 && (
            <div className={styles.staffShifts}>
              <StaffShifts staffShifts={staffShifts} editableStaff={editableStaff} isKiosk={isKiosk} type="RoomShift" />
            </div>
          )}
        </Box>
      </StaffSlotRoomContext>
    </StaffShiftRoomContext>
  );
};

const useStyles = makeStyles(theme => ({
  roomTitle: { fontWeight: 'bold' },
  staffShifts: { marginRight: 20 },
}));

export const TimelineRoom = TimelineRoomComponent;
